import { Modal } from "@components/modals";
import {
	getTabType,
	NoteFormat,
	updateAWorkflowEditorTabInfo,
	WorkflowCanvasTabInfo,
} from "@store/canvas";
import { RootState } from "@store/types";
import {
	saveCurrentWorkflowTabState,
	setCurrentWorkflowStateToUnsaved,
	setWorkflowsListInStore,
} from "@store/workflow";
import { useGetActiveTabInfo } from "@utils/get-active-tab-info";
import classNames from "classnames";
import { cloneDeep } from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedValue, useDidUpdate } from "rooks5";
import { NoteEditor } from "./NoteEditor";
import styles from "./styles.module.scss";

type Props = {
	isModalOpen: boolean;
	closeModal: () => void;
	visible: boolean;
	onNotesFullscreen?: (isFullscreen: boolean) => void;
};

export const WorkflowNote = ({ isModalOpen, closeModal, visible, onNotesFullscreen }: Props) => {
	const [notes, setNotes] = useState("");
	const [notesFormat, setNotesFormat] = useState<NoteFormat>("text");
	const [debouncedNotes] = useDebouncedValue(notes, 500);
	const [activeWorkflowId, setActiveWorkflowId] = useState<number>();
	const activeTabInfo = useGetActiveTabInfo("workflowEditor") as
		| WorkflowCanvasTabInfo
		| undefined;
	const { openTabs } = useSelector(
		(store: RootState) => store.CanvasReducer.workflowEditor
	);
	const { workflows } = useSelector(
		(store: RootState) => store.WorkflowReducer
	);
	const dispatch = useDispatch();

	// To handle undo/redo
	const notesHistory = useRef<string[]>([]);
	const historyIndex = useRef(0);
	const notesRef = useRef("");

	const undoOrRedo = useCallback((e: KeyboardEvent) => {
		if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "z") {
			e.preventDefault();
			historyIndex.current =
				historyIndex.current + 1 < notesHistory.current.length
					? historyIndex.current + 1
					: notesHistory.current.length - 1;
			notesRef.current = notesHistory.current[historyIndex.current];
			setNotes(notesHistory.current[historyIndex.current]);
			return;
		}

		if ((e.ctrlKey || e.metaKey) && e.key === "z") {
			e.preventDefault();
			historyIndex.current =
				historyIndex.current - 1 > 0 ? historyIndex.current - 1 : 0;
			notesRef.current = notesHistory.current[historyIndex.current];
			setNotes(notesHistory.current[historyIndex.current]);
		}
	}, []);

	useEffect(() => {
		if (activeWorkflowId === activeTabInfo?.id) {
			return;
		}
		setActiveWorkflowId(activeTabInfo?.id);
		const detailsStr = typeof activeTabInfo?.details === "string" ? activeTabInfo.details : undefined;

		let notesFromDetails = "";
		let formatFromDetails: NoteFormat | undefined;
		if (detailsStr) {
			try {
				const detailsObj = JSON.parse(detailsStr);
				notesFromDetails = detailsObj.workflowMetaData?.notes || "";
				formatFromDetails = detailsObj.workflowMetaData?.notesFormat;
			} catch {
				notesFromDetails = "";
			}
		}

		const notesFromActiveTab = activeTabInfo?.notes || "";

		const finalNotes = notesFromDetails !== "" ? notesFromDetails : notesFromActiveTab;

		setNotes(finalNotes);
		notesRef.current = finalNotes;
		setNotesFormat(
			finalNotes.trim()
				? formatFromDetails || activeTabInfo?.notesFormat || "text"
				: "text"
		);

		notesHistory.current = [finalNotes];
		historyIndex.current = 0;
	}, [activeTabInfo]);

	const onSave = useCallback(
		(note: string, format: NoteFormat = notesFormat) => {
			const selectedWorkflowData =
				activeTabInfo && openTabs.get(activeTabInfo.id);
			selectedWorkflowData &&
				dispatch(
					updateAWorkflowEditorTabInfo({
						type: getTabType(
							"workflowEditor",
							selectedWorkflowData.info.env
						),
						info: {
							...selectedWorkflowData.info,
							notes: note,
							notesFormat: format,
						},
					})
				);

			const updatedWorkflows = cloneDeep(workflows);
			updatedWorkflows.results = updatedWorkflows.results.map(
				(workflow) => {
					workflow.notes =
						workflow.id === activeTabInfo?.id
							? note
							: workflow.notes;
					return workflow;
				}
			);

			// Push note to history only if it's not undo / redo change
			if (note !== notesHistory.current[historyIndex.current]) {
				historyIndex.current += 1;
				notesHistory.current = notesHistory.current.slice(
					0,
					historyIndex.current
				);
				notesHistory.current = [...notesHistory.current, note];
			}

			dispatch(setWorkflowsListInStore(updatedWorkflows));
			dispatch(setCurrentWorkflowStateToUnsaved());
			dispatch(saveCurrentWorkflowTabState());
		},
		[activeTabInfo, openTabs, dispatch, notesFormat]
	);

	const onFormatChange = useCallback(
		(format: NoteFormat) => {
			setNotesFormat(format);
			onSave(notesRef.current, format);
		},
		[onSave]
	);

	const onChange = (note: string) => {
		setNotes(note);
		notesRef.current = note;
	};

	useDidUpdate(() => {
		if (notes === activeTabInfo?.notes) {
			return;
		}
		onSave(notes);
	}, [debouncedNotes]);

	const onFocus = () => {
		document.addEventListener("keydown", undoOrRedo);
	};

	const onBlur = () => {
		document.removeEventListener("keydown", undoOrRedo);
	};

	const sharedEditorProps = {
		note: notes,
		onChange,
		hideActionButtons: true,
		onFocus,
		onBlur,
		formatsEnabled: true,
		resetKey: activeWorkflowId,
		noteFormat: notesFormat,
		onFormatChange,
		onFullscreenChange: onNotesFullscreen,
	};

	return (
		<div
			className={classNames(styles.workflowNoteWrapper, {
				[styles.visible]: visible,
			})}
		>
			<NoteEditor
				{...sharedEditorProps}
				placeholder="Add notes to yourself here..."
				onSave={onSave}
				isVisible={visible}
			/>

			<Modal
				title={
					<span className={styles.modalTitle}>
						{activeTabInfo?.name}
					</span>
				}
				image="/icons/notification/workflow.svg"
				subtitle={<span className={styles.modalSubtitle}>NOTES</span>}
				className={styles.modal}
				isOpen={isModalOpen}
				toggleClose={closeModal}
				showCloseMark
			>
				<div className="properties__fields__container">
					<NoteEditor
						{...sharedEditorProps}
						onSave={(noteText: string) => {
							onSave(noteText);
							closeModal();
						}}
						isVisible={isModalOpen}
						hideFullscreen
					/>
				</div>
			</Modal>
		</div>
	);
};
